import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/Icon";
import { EmptyState, ErrorState } from "@/components/ui/States";
import { ProjectionLag } from "@/components/ui/ProjectionLag";
import { SegmentedFilter } from "@/components/ui/Toolbar";
import { useProjectionStatus } from "@/lib/api/reports";
import {
  CELL_STATE_LABEL,
  cellState,
  EMPTY_HEAT_SCALE,
  useAisleCells,
  useCellContents,
  useMapCells,
  useMapHeat,
  useMapSections,
  type CellContentRow,
  type CellState,
  type HeatScale,
  type MapCell,
  type MapSection,
} from "@/lib/api/map";
import { qty } from "@/lib/format";
import { useBarcodeScanner } from "@/lib/devices/scanner";
import { PrintButton } from "@/components/ui/PrintButton";
import { MapPlan } from "./MapPlan";
import { MapSummary } from "./MapSummary";
import { AisleScene } from "./AisleScene";
import { AislePlan } from "./AislePlan";
import { CellPallets } from "./CellPallets";
import {
  COLOR_MODE_LABEL,
  heatBands,
  type ColorMode,
  type HeatIndex,
} from "./tone";
import { describeAddress, describeAisle, describeSection, cellSize, zoneName } from "@/lib/api/address";
import "./map.css";

/**
 * Ombor xaritasi.
 *
 * Nimani ko'rsatadi: reyestrdagi HAR BIR yacheykani o'z o'rnida —
 * qator, stellaj, qavat va yacheyka bo'yicha — va uning holatini
 * (bo'sh, band, bloklangan, nofaol).
 *
 * Nega jadval emas: 14 430 qatorli ro'yxatda "qaysi tomon to'lgan"
 * degan savolga javob yo'q. Chizmada esa u bir qarashda ko'rinadi
 * va aynan shu savol slotting va to'ldirish qarorlarining
 * boshlanishi.
 *
 * Ma'lumot CQRS read modelidan keladi va u manbadan biroz orqada
 * qoladi; kechikish yuqorida OCHIQ ko'rsatiladi.
 */
export function WarehouseMap() {
  const sections = useMapSections();
  const status = useProjectionStatus();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedCell, setSelectedCell] = useState<MapCell | null>(null);
  // Stellajli qatorda chizma HAR DOIM old ko'rinish; 3D esa
  // o'sha qatorning uch o'lchamli ko'rinishi.
  const [view, setView] = useState<"plan" | "scene">("plan");
  const [scanMiss, setScanMiss] = useState<string | null>(null);
  // Rang nimani bildiradi. Sukut — HOLAT: kundalik ishda
  // «joy bormi, bloklanganmi» degan savol birinchi turadi.
  const [mode, setMode] = useState<ColorMode>("state");
  const [heatDays, setHeatDays] = useState(30);

  const items = sections.data?.items ?? [];
  const byWarehouse = useMemo(() => groupByWarehouse(items), [items]);
  const groups = useMemo(
    () => byWarehouse.flatMap((warehouse) => warehouse.groups),
    [byWarehouse],
  );

  // Birinchi tanlov AVTOMATIK ochiladi: bo'sh chizma bilan
  // ochilgan xarita foydalanuvchidan sababsiz bitta qo'shimcha
  // bosishni talab qilardi.
  const activeKey = selectedKey ?? groups[0]?.key ?? null;
  const active = groups.find((group) => group.key === activeKey) ?? null;
  const activeWarehouse = items.find(
    (item) => item.aisle === activeKey || item.section === activeKey,
  )?.warehouse_code;

  // Stellajli qator — butun qator (barcha qavat); qolganlari —
  // bo'lak (chiziqli ro'yxat).
  const isAisle = active?.kind === "aisle";
  const aisleCells = useAisleCells(isAisle ? activeKey : null, activeWarehouse);
  const cells = useMapCells(isAisle ? null : activeKey, activeWarehouse);
  const section = items.find((item) => item.section === activeKey);

  // Harakat ro'yxati ALOHIDA so'raladi va faqat «Harakat»
  // ko'rinishida: oyna o'zgarganda butun chizmani emas, shu
  // ro'yxatni qayta tortish kifoya.
  const heatQuery = useMapHeat(
    isAisle ? null : activeKey,
    isAisle ? activeKey : null,
    activeWarehouse,
    heatDays,
    mode === "heat",
  );
  const heat = useMemo(() => {
    const index: HeatIndex = new Map();
    for (const point of heatQuery.data?.items ?? []) {
      index.set(point.location_id, { moves: point.moves, picks: point.picks });
    }
    return { index, scale: heatQuery.data?.scale ?? EMPTY_HEAT_SCALE };
  }, [heatQuery.data]);

  /**
   * Skanerlangan yacheyka kodi — chizmadagi o'z o'rniga olib
   * boradi.
   *
   * Ombor xodimi katakni QO'LIDA ushlab turadi va uni chizmadan
   * ko'z bilan qidirishi ma'nosiz: kodni skanerlash bir
   * harakatda kerakli qavatni ochadi va katakni tanlaydi.
   *
   * Bo'lak KODdan hisoblanadi (TZ §5.1) — server bilan bir xil
   * qoida bo'yicha; topilmasa, kod OCHIQ aytiladi, jimgina
   * tashlab yuborilmaydi.
   */
  useBarcodeScanner((code) => {
    const normalized = code.trim().toUpperCase();
    const target = sectionOfCode(normalized, items);
    if (!target) {
      setScanMiss(normalized);
      return;
    }
    setScanMiss(null);
    setSelectedKey(target);
    setSelectedCell(null);
    setPendingCode(normalized);
  });

  const [pendingCode, setPendingCode] = useState<string | null>(null);

  // Bo'lak kataklari kelgach, skanerlangan katak tanlanadi.
  // Tanlash DARHOL bajarilmaydi: skanerlash paytida kataklar
  // hali yuklanmagan bo'lishi mumkin.
  useEffect(() => {
    if (!pendingCode) return;
    const loaded = [...(aisleCells.data?.items ?? []), ...(cells.data?.items ?? [])];
    const found = loaded.find((cell) => cell.code === pendingCode);
    if (found) {
      setSelectedCell(found);
      setPendingCode(null);
    }
  }, [pendingCode, cells.data, aisleCells.data]);

  if (sections.isError) {
    return <ErrorState error={sections.error} onRetry={() => void sections.refetch()} />;
  }

  return (
    <>
      <PageHeader
        title="Ombor xaritasi"
        hint="Qator, stellaj, qavat va yacheyka chizmasi. Rang — katakning holati."
        actions={
          <>
            <PrintButton label="Chizmani chop etish" />
            <ProjectionLag status={status.data?.items} />
          </>
        }
      />

      {/* Yig'indi VAZIFA kesimida: «ombor to'ldimi» degan savolga
          MX va OX birgalikda javob bermaydi — saqlash to'lib,
          terish bo'sh turishi mumkin va bu ikki boshqa muammo. */}
      <MapSummary />

      {scanMiss && (
        <div className="notice">
          <Icon name="scan" size={16} />
          <span>
            Skanerlangan kod xaritada topilmadi:{" "}
            <span className="mono">{scanMiss}</span>. Reyestrda bunday yacheyka
            yo'q yoki hisobot hali yangilanmagan.
          </span>
        </div>
      )}

      <div className="map-layout">
        <aside className="group glass-thin map-sections">
          {byWarehouse.length === 0 && !sections.isLoading && (
            <EmptyState
              icon="map"
              title="Reyestr bo'sh"
              hint="Yacheykalar importidan keyin xarita o'zi to'ladi."
            />
          )}

          {byWarehouse.map((warehouse) => (
            <div className="map-wh" key={warehouse.code}>
              <div className="map-wh-label">
                <span>{warehouse.code} korpus</span>
                <span>{warehouse.cells} ta</span>
              </div>
              <div className="map-section-list">
                {warehouse.groups.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`map-section${item.key === activeKey ? " active" : ""}`}
                    onClick={() => {
                      setSelectedKey(item.key);
                      setSelectedCell(null);
                    }}
                  >
                    <span className="map-section-head">
                      <span>{item.label}</span>
                      <span className="num">{percent(item.occupied, item.cells)}</span>
                    </span>
                    <span className="map-fill">
                      <span
                        className="map-fill-bar"
                        style={{ width: percent(item.occupied, item.cells) }}
                      />
                    </span>
                    <span className="map-section-sub num">
                      {item.cells} katak · {item.occupied} band
                      {item.blocked > 0 ? ` · ${item.blocked} blok` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <div className="map-plan">
          <div className="group glass-thin">
            <div className="map-plan-head">
              <div>
                <div className="group-title">{active?.label ?? "Chizma"}</div>
                <div className="group-hint">
                  {active
                    ? isAisle
                      ? `${active.cells} katak · ${active.occupied} band · stellajlar old tomondan`
                      : section
                        ? planHint(section)
                        : `${active.cells} o'rin`
                    : "Bo'lim tanlang"}
                </div>
              </div>
              <div className="map-plan-tools">
                {/* Rang nimani bildiradi. Uch savol — uch
                    ko'rinish; ularni bitta chizmada birlashtirish
                    rangni o'qilmaydigan holga keltirardi. */}
                <SegmentedFilter
                  value={mode}
                  onChange={setMode}
                  options={(["state", "abc", "heat"] as ColorMode[]).map((value) => ({
                    value,
                    label: COLOR_MODE_LABEL[value],
                  }))}
                />

                {/* Oyna faqat harakat ko'rinishida: holat va ABC
                    ayni damdagi holatni ko'rsatadi, ularda
                    «qancha kun» degan savol yo'q. */}
                {mode === "heat" && (
                  <SegmentedFilter
                    value={String(heatDays)}
                    onChange={(value) => setHeatDays(Number(value))}
                    options={[
                      { value: "7", label: "7 kun" },
                      { value: "30", label: "30 kun" },
                      { value: "90", label: "90 kun" },
                    ]}
                  />
                )}

                {/* 3D faqat stellajli qatorda ma'noga ega: yerda
                    saqlash va operatsion zonalar bir qavatli. */}
                {isAisle && (
                  <SegmentedFilter
                    value={view}
                    onChange={setView}
                    options={[
                      { value: "plan", label: "Old ko'rinish" },
                      { value: "scene", label: "3D" },
                    ]}
                  />
                )}
              </div>
            </div>

            {/* Stellajli qator: old ko'rinish yoki 3D.
                Qolganlari (yerda saqlash, operatsion zona) —
                chiziqli ro'yxat, ularda qavat tuzilishi yo'q. */}
            {isAisle ? (
              aisleCells.isError ? (
                <ErrorState error={aisleCells.error} onRetry={() => void aisleCells.refetch()} />
              ) : view === "scene" ? (
                aisleCells.isLoading ? (
                  <div className="skeleton" style={{ height: 420 }} />
                ) : (
                  <AisleScene
                    cells={aisleCells.data?.items ?? []}
                    selected={selectedCell}
                    onSelect={setSelectedCell}
                    mode={mode}
                    heat={heat}
                  />
                )
              ) : (
                <AislePlan
                  cells={aisleCells.data?.items ?? []}
                  loading={aisleCells.isLoading}
                  selected={selectedCell}
                  onSelect={setSelectedCell}
                  mode={mode}
                  heat={heat}
                />
              )
            ) : cells.isError ? (
              <ErrorState error={cells.error} onRetry={() => void cells.refetch()} />
            ) : (
              <MapPlan
                section={section}
                cells={cells.data?.items ?? []}
                loading={cells.isLoading}
                truncated={cells.data?.has_more ?? false}
                cellSize={18}
                selected={selectedCell}
                onSelect={setSelectedCell}
                mode={mode}
                heat={heat}
              />
            )}

            <Legend mode={mode} scale={heat.scale} days={heatQuery.data?.window_days ?? heatDays} />
          </div>

        </div>
      </div>

      {/* Katak tafsiloti MODAL: ilgari u chizmaning yonida turardi
          va uzun tarkib ro'yxati chizmani ekrandan siqib
          chiqarardi. Modal esa chizmani joyida qoldiradi —
          yopilgach xodim o'sha yerdan davom etadi. */}
      {selectedCell && (
        <CellModal
          cell={selectedCell}
          heat={heat.index.get(selectedCell.location_id)}
          heatDays={heatQuery.data?.window_days ?? heatDays}
          onClose={() => setSelectedCell(null)}
        />
      )}
    </>
  );
}


const LEGEND: CellState[] = ["empty", "partial", "full", "blocked", "no-putaway", "inactive"];

/**
 * Legenda KO'RINISH bilan birga almashadi.
 *
 * Harakat ko'rinishida u «kam / ko'p» demaydi, AYNAN SONLARNI
 * yozadi: rangning ortida qanday son turganini bilmagan xodim
 * chizmadan qaror chiqara olmasdi.
 */
function Legend({ mode, scale, days }: { mode: ColorMode; scale: HeatScale; days: number }) {
  if (mode === "abc") {
    return (
      <div className="map-legend">
        <span className="map-legend-item">
          <span className="map-swatch map-cell abc-a" />A toifa
        </span>
        <span className="map-legend-item">
          <span className="map-swatch map-cell abc-b" />B toifa
        </span>
        <span className="map-legend-item">
          <span className="map-swatch map-cell abc-c" />C toifa
        </span>
        <span className="map-legend-item">
          <span className="map-swatch map-cell zone-a" />
          Bo'sh — hudud rangi
        </span>
        <span className="map-legend-item">
          <span className="map-swatch map-cell abc-a tone-mismatch" />
          O'z hududida emas
        </span>
        <span className="map-legend-note">
          Rang — katakda TURGAN tovarning toifasi; bo'sh katak o'zining
          loyihadagi hududini ko'rsatadi.
        </span>
      </div>
    );
  }

  if (mode === "heat") {
    return (
      <div className="map-legend">
        {heatBands(scale).map((band) => (
          <span className="map-legend-item" key={band.key}>
            <span className={`map-swatch map-cell ${band.key}`} />
            {band.label}
          </span>
        ))}
        <span className="map-legend-note">
          {scale.cells > 0
            ? `${days} kun · ${scale.cells} katakda harakat bor, eng ko'pi ${scale.max} marta. ` +
              "Shkala butun korpus bo'yicha — qatorlarni solishtirsa bo'ladi."
            : `${days} kun ichida hech bir katakka tegilmagan. ` +
              "Harakat tarixi birinchi qabuldan keyin to'la boshlaydi."}
        </span>
      </div>
    );
  }

  return (
    <div className="map-legend">
      {LEGEND.map((state) => (
        <span className="map-legend-item" key={state}>
          <span className={`map-swatch map-cell state-${state}`} />
          {CELL_STATE_LABEL[state]}
        </span>
      ))}
      <span className="map-legend-item">
        <span className="map-swatch map-cell-gap" />
        Reyestrda yo'q
      </span>
    </div>
  );
}

/**
 * Tanlangan katak — modal oyna.
 *
 * Ichida nima turgani ALOHIDA so'raladi: chizmada 15 mingta katak
 * bor va har birining tarkibini oldindan yuklash javobni yuzlab
 * barobar kattalashtirardi.
 */
function CellModal({
  cell,
  heat,
  heatDays,
  onClose,
}: {
  cell: MapCell;
  /** Harakat ko'rinishi ochilmagan bo'lsa bo'sh. */
  heat?: { moves: number; picks: number };
  heatDays: number;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contents = useCellContents(cell.location_id);
  const state = cellState(cell);
  const goods = (cell.sku_abc_class ?? "").toUpperCase();
  const zone = (cell.abc_zone ?? "").toUpperCase();
  const misplaced = goods !== "" && zone !== "" && goods !== zone;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const size = cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm);
  const cellVolume = size.length * size.width * size.height;
  const limit = Number(cell.max_weight_kg);
  const data = contents.data;
  // VGH si yo'q tovar yig'indiga KIRMAYDI — shuning uchun
  // «to'liqmi» degan savol alohida turadi.
  const partial = data ? data.measured_skus < data.total_skus : false;
  const known = (data?.measured_skus ?? 0) > 0;

  // Vazn chegarasi QAT'IY: oshgani xavfsizlik masalasi.
  const overWeight = known && limit > 0 && Number(data!.total_weight_kg) > limit;
  // Hajm esa YALPI o'lcham bilan solishtiriladi (uzunlik × en ×
  // balandlik): katakni 100% zich to'ldirib bo'lmaydi, shuning
  // uchun bu chegara emas, belgi.

  return (
    <dialog ref={dialogRef} className="dialog dialog-wide dialog-card glass-thick" onCancel={onClose}>
      <div className="dialog-head">
        <span className={`dialog-icon cell-chip ${`state-${state}`}`} aria-hidden />
        <div>
          <div className="dialog-title mono">{cell.code}</div>
          {/* Manzil OCHIB yoziladi: kodni har safar yechish
              xodimdan ortiqcha ish talab qilardi. */}
          <div className="dialog-desc">{describeAddress(cell.code)}</div>
          <div className="dialog-desc">
            {zoneName(cell.zone_code)} · {cell.location_function} · {cell.cell_type}
            {cell.abc_zone ? ` · ${cell.abc_zone} hudud` : ""} · {CELL_STATE_LABEL[state]}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-icon"
          style={{ marginLeft: "auto" }}
          onClick={onClose}
          aria-label="Yopish"
        >
          <Icon name="close" />
        </button>
      </div>

      <div className="dialog-body">
        <div className="cell-sums">
          <CellSum label="Qoldiq" value={qty(data?.total_qty ?? cell.on_hand_qty)} />
          <CellSum
            label="Og'irlik"
            tone={overWeight ? "error" : undefined}
            value={
              known
                ? limit > 0
                  ? `${qty(data!.total_weight_kg)} / ${qty(cell.max_weight_kg)} kg`
                  : `${qty(data!.total_weight_kg)} kg`
                : "—"
            }
            hint={
              !known && (data?.total_skus ?? 0) > 0
                ? "tovarlarda VGH yo'q"
                : overWeight
                  ? "chegaradan oshgan"
                  : limit > 0
                    ? "katak chegarasi bilan"
                    : "katak chegarasi ko'rsatilmagan"
            }
          />
          <CellSum
            label="Hajm"
            tone={known && Number(data!.total_volume_cm3) > cellVolume ? "warning" : undefined}
            value={known ? `${volume(Number(data!.total_volume_cm3))} / ${volume(cellVolume)}` : "—"}
            hint={
              !known && (data?.total_skus ?? 0) > 0
                ? "tovarlarda VGH yo'q"
                : `katakning yalpi o'lchami ${size.length}×${size.width}×${size.height} sm`
            }
          />
          <CellSum
            label="Pallet o'rni"
            value={
              cell.capacity_pallets == null
                ? "pallet emas"
                : `${cell.occupied_slots} / ${cell.capacity_pallets}`
            }
          />
        </div>

        {partial && (
          <div className="notice" role="status">
            <Icon name="alert" size={16} />
            <span>
              {data!.total_skus - data!.measured_skus} ta tovarda VGH yo'q — og'irlik va
              hajm yig'indisi ularni hisobga olmaydi.
            </span>
          </div>
        )}

        <div className="cell-section">Katakdagi tovarlar</div>
        {contents.isLoading ? (
          <div className="skeleton" style={{ height: 96 }} />
        ) : contents.error ? (
          <ErrorState error={contents.error} onRetry={() => void contents.refetch()} />
        ) : (data?.items.length ?? 0) === 0 ? (
          <p className="field-hint" style={{ margin: 0 }}>
            Katak bo'sh — qoldiq yo'q.
          </p>
        ) : (
          <table className="data" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Tovar</th>
                <th style={{ textAlign: "right" }}>Qoldiq</th>
                <th style={{ textAlign: "right" }}>Band</th>
                <th style={{ textAlign: "right" }}>Og'irlik</th>
                <th style={{ textAlign: "right" }}>Hajm</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((row) => (
                <ContentRow key={row.sku_id} row={row} />
              ))}
            </tbody>
          </table>
        )}

        {(cell.occupied_slots > 0 || Number(cell.on_hand_qty) > 0) && (
          <>
            <div className="cell-section">Palletlar (LP) va buyurtma</div>
            <CellPallets locationId={cell.location_id} />
          </>
        )}

        <div className="cell-section">Katak haqida</div>
        <dl className="kv">
          <dt>O'lcham</dt>
          <dd className="num">{sizeText(cell)}</dd>

          {goods && (
            <>
              <dt>Tovar toifasi</dt>
              <dd>
                {goods} toifa
                {misplaced && <span className="tone-warn"> · {zone} hududida turibdi</span>}
              </dd>
            </>
          )}

          {heat && (
            <>
              <dt>Harakat ({heatDays} kun)</dt>
              <dd className="num">
                {heat.moves} marta
                {heat.picks > 0 ? ` · ${heat.picks} terish` : ""}
              </dd>
            </>
          )}

          <dt>Joylashtirish</dt>
          <dd>{cell.putaway_allowed ? "ruxsat etilgan" : "yopiq"}</dd>

          <dt>Faollik</dt>
          <dd>{cell.is_active ? "faol" : "nofaol"}</dd>
        </dl>
      </div>

      <div className="dialog-actions">
        <button type="button" className="btn btn-filled" onClick={onClose}>
          Yopish
        </button>
      </div>
    </dialog>
  );
}

function CellSum({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Chegaradan oshgan qiymat RANG bilan ajraladi. */
  tone?: "error" | "warning";
}) {
  return (
    <div className="cell-sum">
      <span className="cell-sum-label">{label}</span>
      <span className={`cell-sum-value num${tone ? ` tone-${tone}` : ""}`}>{value}</span>
      {hint && <span className="cell-sum-hint">{hint}</span>}
    </div>
  );
}

function ContentRow({ row }: { row: CellContentRow }) {
  return (
    <tr>
      <td>
        <div className="mono">{row.article}</div>
        <div className="field-hint" style={{ margin: 0 }}>
          {row.name}
          {row.lp_count > 1 ? ` · ${row.lp_count} LP` : ""}
          {row.lot_count > 0 ? ` · ${row.lot_count} partiya` : ""}
        </div>
      </td>
      <td style={{ textAlign: "right" }} className="num">
        {qty(row.on_hand_qty)}
      </td>
      <td style={{ textAlign: "right" }} className="num">
        {qty(row.reserved_qty)}
      </td>
      {/* VGH yo'q bo'lsa NOL emas, ochiq yozuv: nol «og'irligi
          yo'q» degan ma'no berardi va yuk chegarasi bo'yicha
          noto'g'ri qaror chiqardi. */}
      <td style={{ textAlign: "right" }} className={row.has_vgh ? "num" : ""}>
        {row.has_vgh ? `${qty(row.weight_kg)} kg` : <span className="cell-unknown">VGH yo'q</span>}
      </td>
      <td style={{ textAlign: "right" }} className={row.has_vgh ? "num" : ""}>
        {row.has_vgh ? volume(Number(row.volume_cm3)) : <span className="cell-unknown">—</span>}
      </td>
    </tr>
  );
}

/**
 * Hajm o'qiladigan birlikda.
 *
 * Santimetr kubda ombor hajmi yetti xonali son bo'lib chiqadi va
 * uni ko'z bilan solishtirib bo'lmaydi. Kichik hajm litrda,
 * kattasi kub metrda yoziladi — xodim shu birliklarda o'ylaydi.
 */
function volume(cm3: number): string {
  if (!Number.isFinite(cm3) || cm3 <= 0) return "0";
  if (cm3 >= 1_000_000) return `${(cm3 / 1_000_000).toLocaleString("uz", { maximumFractionDigits: 2 })} m³`;
  return `${(cm3 / 1000).toLocaleString("uz", { maximumFractionDigits: 1 })} l`;
}

/**
 * Katak o'lchami matni.
 *
 * Reyestrda o'lcham bo'lmasa katak TURIDAN nominal qiymat
 * olinadi va bu OCHIQ aytiladi: taxminiy o'lchamga qarab
 * sig'im qarori qabul qilinmasligi kerak.
 */
function sizeText(cell: MapCell): string {
  const size = cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm);
  const text = `${size.length}×${size.width}×${size.height} sm`;
  return size.measured ? text : `${text} (katak turidan)`;
}

/**
 * Yon paneldagi bitta tanlov.
 *
 * Stellajli korpusda bu QATOR (A01..A10), qavat emas: ombor
 * xodimi «birinchi qatorga boraman» deb o'ylaydi va «qator →
 * qavat» ikki bosqichli ro'yxat chalkashtirardi. Qavatlar endi
 * chizmaning O'ZIDA, stellaj ustma-ust ko'rinishida.
 */
interface MapGroup {
  /** Kataklarni so'rash kaliti. */
  key: string;
  kind: "aisle" | "section";
  label: string;
  cells: number;
  occupied: number;
  blocked: number;
}

/** Tanlovlarni korpus bo'yicha guruhlaydi. */
function groupByWarehouse(sections: MapSection[]) {
  const map = new Map<string, { code: string; cells: number; groups: Map<string, MapGroup> }>();

  for (const section of sections) {
    const entry = map.get(section.warehouse_code) ?? {
      code: section.warehouse_code,
      cells: 0,
      groups: new Map<string, MapGroup>(),
    };
    entry.cells += section.cell_count;

    // RACK: qavatlar bitta qatorga YIG'ILADI.
    const aisle = section.kind === "RACK" ? section.aisle : undefined;
    const key = aisle ?? section.section;
    const group = entry.groups.get(key) ?? {
      key,
      kind: aisle ? "aisle" : "section",
      label: aisle ? describeAisle(aisle) : sectionLabel(section),
      cells: 0,
      occupied: 0,
      blocked: 0,
    };
    group.cells += section.cell_count;
    group.occupied += section.occupied_cells;
    group.blocked += section.blocked_cells;
    entry.groups.set(key, group);
    map.set(section.warehouse_code, entry);
  }

  return [...map.values()].map((entry) => ({
    code: entry.code,
    cells: entry.cells,
    groups: [...entry.groups.values()].sort((a, b) => a.key.localeCompare(b.key)),
  }));
}

function percent(part: number, total: number): string {
  if (total <= 0) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

/**
 * Bo'lak sarlavhasi.
 *
 * Stellajli korpusda bo'lak — bitta QAVAT, va uni "A01-03" deb
 * ko'rsatish kod bilan chalkashtirardi. Shuning uchun u
 * "A01 qator · 3-qavat" deb yoziladi.
 */
function sectionLabel(section: MapSection): string {
  // «A01-03» yoki «JOY» xodimga hech nima demaydi — ochib yoziladi.
  if (section.kind === "RACK") return describeSection(section.section);
  return zoneName(section.section);
}


/**
 * Yacheyka kodidan xarita tanlovini topadi.
 *
 * Qoida serverdagi bilan BIR XIL (TZ §5.1): stellajli korpusda
 * tanlov — QATOR (qavat emas: qavatlar bitta chizmada), qolgan
 * joylarda — zona. Tanlov topilgach u mavjud ro'yxat bilan
 * solishtiriladi: reyestrda yo'q bo'lakni ochish bo'sh chizma
 * ko'rsatardi.
 */
function sectionOfCode(code: string, sections: MapSection[]): string | undefined {
  const rack = /^([A-Z][0-9]{2})-[0-9]{2}-[0-9]{2}-[0-9]{2}$/.exec(code);
  if (rack) {
    return sections.find((section) => section.aisle === rack[1])?.aisle;
  }

  // Chiziqli manzillar: bo'lak = zona. Kodning o'zidan zonani
  // aniqlab bo'lmaydi (B042 dagi "B" korpus, zona emas), shuning
  // uchun katak KODI bo'yicha emas, prefiks bo'yicha qidiriladi.
  const operational = /^([A-Z]+)-[0-9]+$/.exec(code);
  if (operational) {
    return sections.find((section) => section.section === operational[1])?.section;
  }

  const floor = /^([A-Z])[0-9]{3}$/.exec(code);
  if (floor) {
    return sections.find(
      (section) => section.kind === "FLOOR" && section.warehouse_code === floor[1],
    )?.section;
  }
  return undefined;
}

function planHint(section: MapSection): string {
  if (section.kind === "RACK") {
    return `${section.max_row} stellaj × ${section.max_col} yacheyka · ${section.cell_count} katak`;
  }
  if (section.kind === "FLOOR") {
    return `yerda saqlash · ${section.cell_count} o'rin`;
  }
  return `${zoneName(section.zone_code)} · ${section.cell_count} o'rin`;
}
